import { fetchWithAuth, getApiBase } from '@/lib/api';
import type { ModelSlot } from '@/lib/api/admin-models.client';

export type ModelRegion = 'de' | 'eu' | 'us' | 'uk' | 'ch' | 'global';

export type ModelCostCategory = '€' | '€€' | '€€€' | '€€€€';
export type ModelClass = 'premium' | 'standard';

export type ModelVisibility = 'public' | 'preview' | 'hidden' | 'local';

export interface ChatModelInfo {
  id: string;
  name: string;
  provider?: string;
  /** Model maker id (a key of the vendors section in models.yaml). */
  vendor?: string;
  /** Display name of the model maker, from models.yaml. */
  vendor_name?: string;
  region?: ModelRegion;
  location?: string;
  capabilities?: string[];
  multimodal?: boolean;
  eu_sovereign?: boolean;
  cost_category?: ModelCostCategory;
  model_class?: ModelClass;
  low_budget_allowed?: boolean;
  intelligence_index?: number;
  avg_cost_per_request?: number;
  open_weights?: boolean;
  visibility?: ModelVisibility;
  /**
   * Computed per-request by GET /models: true when the current TRIAL user may not
   * use this model. Drives the lock in the model selector. Always false for paid users.
   */
  trial_locked?: boolean;
  /** A public chat can give fast answers on it: its thinking can be switched off (KAN-1454). */
  supports_thinking_off?: boolean;
  /** Day (YYYY-MM-DD) the model was switched on for everyone; marked new for a week (KAN-1756). */
  available_since?: string | null;
}

export interface RecommendedModel {
  tier: 'good' | 'medium';
  model_id: string;
}

export interface TierNotice {
  /** A chat tier or an image purpose (KAN-1251). */
  tier: ModelSlot;
  /** changed: the recommendation moved on; unavailable: the tier's model cannot be used. */
  kind: 'changed' | 'unavailable';
  workspace_model_id: string;
  recommended_model_id: string;
  /** The model is only switched off in the workspace; switching it on fixes it. */
  can_enable: boolean;
  /** The workspace has its own model and the 9brains one is usable instead. */
  offer_recommendation: boolean;
}

/** The user's own model for one tier (Premium = good, Standard = medium, or an image slot). */
export interface PersonalTier {
  tier: ModelSlot;
  personal_model_id: string | null;
  /** False when the stored personal model is currently unavailable to the user. */
  personal_model_usable: boolean;
  /** What the user gets for this tier without a personal model. */
  workspace_model_id: string | null;
  notice: boolean;
  auto_switch_at: string | null;
}

/** The user's own models (chat tiers and image) and pending "recommendation changed" notices. */
export interface ModelPreference {
  can_choose: boolean;
  tiers: PersonalTier[];
  /** Workspace tiers to review; only filled for model managers. */
  tenant_notices: TierNotice[];
}

/** The personal model to use for ``tier``, or null to use the workspace's. */
export function effectivePersonalModel(
  pref: ModelPreference | null | undefined,
  tier: 'good' | 'medium'
): string | null {
  if (!pref?.can_choose) return null;
  const state = pref.tiers?.find((t) => t.tier === tier);
  return state?.personal_model_usable ? state.personal_model_id : null;
}

export interface ModelDiscoveryResponse {
  chat_models: ChatModelInfo[];
  default_chat_model: string;
  recommended_models: RecommendedModel[];
  model_preference?: ModelPreference | null;
}

/** The fields of a discovered chat model the app keeps (the store's model list). */
export function toChatModelInfo(model: ChatModelInfo): ChatModelInfo {
  return {
    id: model.id,
    name: model.name,
    provider: model.provider,
    vendor: model.vendor,
    vendor_name: model.vendor_name,
    region: model.region,
    location: model.location,
    capabilities: model.capabilities,
    multimodal: model.multimodal,
    eu_sovereign: model.eu_sovereign,
    cost_category: model.cost_category,
    low_budget_allowed: model.low_budget_allowed,
    intelligence_index: model.intelligence_index,
    avg_cost_per_request: model.avg_cost_per_request,
    visibility: model.visibility,
    trial_locked: model.trial_locked,
    supports_thinking_off: model.supports_thinking_off,
    available_since: model.available_since,
  };
}

export async function fetchModels(): Promise<ModelDiscoveryResponse> {
  const res = await fetchWithAuth(`${getApiBase()}/api/v1/providers/models`);
  return res.json();
}

async function discoveryOrThrow(res: Response): Promise<ModelDiscoveryResponse> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
  return res.json();
}

/** Set the user's own model for a tier; `null` follows the workspace again. */
export async function setModelPreference(
  tier: ModelSlot,
  modelId: string | null
): Promise<ModelDiscoveryResponse> {
  const res = await fetchWithAuth(`${getApiBase()}/api/v1/providers/models/preference`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tier, model_id: modelId }),
  });
  return discoveryOrThrow(res);
}

/** Keep the personal model for a tier although the workspace's changed. */
export async function keepModelPreference(tier: ModelSlot): Promise<ModelDiscoveryResponse> {
  const res = await fetchWithAuth(`${getApiBase()}/api/v1/providers/models/preference/keep`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tier }),
  });
  return discoveryOrThrow(res);
}
